import { useCallback, useEffect, useState } from 'react'
import { PrinterButton } from '../../components/PrinterButton'
import { LogoBase64Image } from '../../constants'
import { ShippingDetailsData } from '../../types/shipping.types'
import { AlertPropsType } from '../../components/ui/alert'
import { fetchShippingDetails } from './ShippingServices'
import { useSelector } from 'react-redux'
import { RootState } from '../../redux/store'
import moment from 'moment'
import { rupiahFormat } from '../../lib/utils'

type UserState = {
  _id: string;
  full_name: string;
  role: string;
  email: string;
  office: {
    city: string;
    name: string;
    code: string;
    address: string;
  }
};

const PrinterButtonWrapper = ({ shippingData, user }: { shippingData: ShippingDetailsData, user: UserState }) => {
  const receiptData = {
    logoPngBase64: LogoBase64Image,
    officeAddress: user.office.address || "Jl. SM Raja no. 40 Medan",
    shippingNumber: shippingData.shipping_number,
    printDate: moment().format("DD/MM/YYYY, HH:mm:ss"),
    cashier: user.full_name,
    paymentStatus: shippingData.payment_status === "paid" ? "LUNAS" : "BELUM LUNAS",
    items: [
      {
        quantity: shippingData.quantity,
        unit: shippingData.item_unit
      }
    ],
    subtotal: shippingData.total_price,
    discount: shippingData.discount_price,
    totalPayment: shippingData.total_price && shippingData.discount_price ? shippingData.total_price - shippingData.discount_price : shippingData.total_price
  };

  const shippingLabelData = {
    logoPngBase64: LogoBase64Image,
    barcodePngBase64: shippingData.shipping_barcode,
    qrCodePngBase64: shippingData.track_shipping_qrcode,
    branchAddress: user.office.address || "Jl. SM Raja no. 40 Medan",
    trackingNumber: shippingData.shipping_number,
    senderName: shippingData.origin_customer_data.sender_name,
    senderAddress: shippingData.origin_customer_data.sender_address,
    recipientName: shippingData.destination_customer_data.recipient_name,
    recipientAddress: shippingData.destination_customer_data.recipient_address,
    itemCount: shippingData.quantity,
    sendDate: shippingData.shipping_date,
    arrivalDate: shippingData.estimation_arrival_date,
    shippingCost: shippingData.total_price,
    discount: shippingData.discount_price,
    totalPayment: shippingData.total_price && shippingData.discount_price ? shippingData.total_price - shippingData.discount_price : shippingData.total_price,
    paymentStatus: shippingData.payment_status === "paid" ? "LUNAS" : "BELUM LUNAS",
    adminName: user.full_name,
  };


  return (
    <div className='flex items-center justify-between'>
      <PrinterButton
        printType="payment_receipt"
        receipt={receiptData}
        presetIndex={2}
        onPrintError={(error) => console.log(error)}
        buttonLabel="Cetak Struk Pembayaran"
      />
      <PrinterButton
        printType="shipping_label"
        shippingLabel={shippingLabelData}
        presetIndex={2}
        onPrintError={(error) => console.log(error)}
        buttonLabel="Cetak Resi Pengiriman"
      />
    </div>
  )
};

const SingleDataPreview = ({ label, value, wrapperClassName }: { label: string; value: string | null; wrapperClassName?: string }) => {
  return (
    <div className={`capitalize ${wrapperClassName}`}>
      <label className='text-neutral-500 text-sm'>{label}</label>
      {label.toLowerCase() === "status pengiriman" ?
        <p className={`font-semibold text-sm leading-5 ${value === "CANCELED" ? "text-red-600" : value === "IN_TRANSIT" ? "text-yellow-500" : "text-green-600"}`}>{value}</p>
        : label.toLowerCase() === "status pembayaran" ?
          <p className={`font-semibold text-sm leading-5 ${value === "unpaid" ? "text-red-600" : "text-green-600"}`}>{value === "paid" ? "LUNAS" : "BELUM BAYAR"}</p>
          : <p className='text-black text-sm font-semibold leading-5'>{value || "-"}</p>
      }
    </div>
  );
};

const ShippingDetails = ({
  shippingNumber
}: {
  shippingNumber: string | undefined
}) => {
  const { user } = useSelector((state: RootState) => state.auth);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [alert, setAlert] = useState<AlertPropsType | null>(null);

  const [shippingData, setShippingData] = useState<ShippingDetailsData | null>(null);

  const getShippingDetails = useCallback(async (shippingCode: string) => {
    try {
      const response = await fetchShippingDetails(shippingCode);
      const { status, message, data } = response;
      if (status === 200) {
        setShippingData(data);
        setIsLoading(false);
      } else {
        setIsLoading(false);
        setAlert({
          variant: "error",
          title: "Terjadi kesalahan",
          message
        });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setIsLoading(false);
      setAlert({
        variant: "error",
        title: "Terjadi kesalahan",
        message: "Silahkan coba beberapa saat lagi"
      });
    }
  }, []);

  useEffect(() => {
    if (shippingNumber) {
      getShippingDetails(shippingNumber)
    };

    return () => { }
  }, [shippingNumber, getShippingDetails]);

  if (!shippingData) {
    return null;
  };

  const {
    // _id,
    // weight_kg,
    quantity,
    item_unit,
    origin_customer_data,
    destination_customer_data,
    shipping_date,
    shipping_number,
    shipping_barcode,
    track_shipping_qrcode,
    status_code,
    // status_label,
    total_price,
    discount_price,
    payment_status,
    origin_office_id,
    destination_office_id
  } = shippingData;

  return (
    <div>
      <div className='px-6 py-4'>
        <div className='flex flex-col lg:flex-row-reverse items-center lg:justify-between'>
          <img
            src={shipping_barcode}
            alt="barcode"
            className='md:h-20 w-full md:w-max'
          />
          <img
            src={track_shipping_qrcode}
            alt="qrcode"
            className='md:h-20 h-0'
          />
        </div>
        <hr className='my-3' />
        <div className='grid grid-cols-1 lg:grid-cols-3 gap-4'>
          <SingleDataPreview label="No. Resi" value={shipping_number} />
          <SingleDataPreview label="Status Pengiriman" value={status_code} />
          <SingleDataPreview label="Status Pembayaran" value={payment_status} />
          <SingleDataPreview label="Asal" value={`${origin_office_id.city} - ${origin_office_id.branch_name}`} />
          <SingleDataPreview label="Tujuan" value={`${destination_office_id.city} - ${destination_office_id.branch_name}`} />
          <hr className='col-span-3 my-1' />
          <SingleDataPreview label="Nama Pengirim" value={origin_customer_data.sender_name} />
          <SingleDataPreview label="No. HP Pengirim" value={origin_customer_data.sender_phone} />
          <SingleDataPreview label="Alamat Pengirim" value={origin_customer_data.sender_address} />
          <hr className='col-span-3 my-1' />
          <SingleDataPreview label="Nama Penerima" value={destination_customer_data.recipient_name} />
          <SingleDataPreview label="No. HP Penerima" value={destination_customer_data.recipient_phone} />
          <SingleDataPreview label="Alamat Penerima" value={destination_customer_data.recipient_address} />
          <hr className='col-span-3 my-1' />
          <SingleDataPreview label="Jumlah Barang" value={`${quantity} ${item_unit}`} />
          <SingleDataPreview label="Tanggal Kirim" value={moment(shipping_date).format("DD MMMM YYYY")} />
          <SingleDataPreview label="Tanggal Tiba" value={moment(shipping_date).add("days", 1).format("DD MMMM YYYY")} />
          <hr className='col-span-3 my-1' />
          <SingleDataPreview label="Ongkos Kirim" value={`Rp ${rupiahFormat(total_price)}`} />
          <SingleDataPreview label="Diskon" value={`Rp ${rupiahFormat(discount_price)}`} />
          <SingleDataPreview label="Total Bayar" value={`Rp ${rupiahFormat(total_price - discount_price)}`} />
        </div>
      </div>
      <div className='sticky bottom-0 px-6 py-6 bg-gray-200'>
        {shippingData && user &&
          <PrinterButtonWrapper shippingData={shippingData} user={user} />
        }
      </div>
    </div>
  )
}

export default ShippingDetails